import { useState, useEffect, useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { 
  Search, 
  Filter, 
  ChevronLeft, 
  ChevronRight, 
  ExternalLink,
  Calendar,
  X,
  RefreshCw,
  Loader2,
  Eye,
  ArrowLeft,
  RotateCcw
} from '@/components/icons'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/context/auth-context'
import { useSettings } from '@/context/settings-context'

// ============================================================================
// TYPES
// ============================================================================

interface ExecutionLog {
  id: string
  event_id: string | null
  automation_rule_id: string | null
  action_type: string
  user_id: string
  execution_result: 'applied' | 'skipped' | 'failed' | 'no_match'
  skip_reason: string | null
  error_message: string | null
  action_details: Record<string, unknown> | null
  event_title: string | null
  event_link: string | null
  event_start_time: string | null
  event_end_time: string | null
  event_is_recurring: boolean
  calendar_id_external: string | null
  event_snapshot: Record<string, unknown> | null
  rule_name: string | null
  rule_priority: number | null
  rule_snapshot: Record<string, unknown> | null
  executed_at: string
}

interface CalendarOption {
  calendar_id_external: string
  label: string
}

// ============================================================================
// CONSTANTS
// ============================================================================

const PAGE_SIZE = 20

// Google Calendar color mapping
const GOOGLE_CALENDAR_COLORS: Record<string, { name: string; bg: string; text: string }> = {
  '1': { name: 'Lavender', bg: 'bg-[#7986cb]', text: 'text-white' },
  '2': { name: 'Sage', bg: 'bg-[#33b679]', text: 'text-white' },
  '3': { name: 'Grape', bg: 'bg-[#8e24aa]', text: 'text-white' },
  '4': { name: 'Flamingo', bg: 'bg-[#e67c73]', text: 'text-white' },
  '5': { name: 'Banana', bg: 'bg-[#f6bf26]', text: 'text-black' },
  '6': { name: 'Tangerine', bg: 'bg-[#f4511e]', text: 'text-white' },
  '7': { name: 'Peacock', bg: 'bg-[#039be5]', text: 'text-white' },
  '8': { name: 'Graphite', bg: 'bg-[#616161]', text: 'text-white' },
  '9': { name: 'Blueberry', bg: 'bg-[#3f51b5]', text: 'text-white' },
  '10': { name: 'Basil', bg: 'bg-[#0b8043]', text: 'text-white' },
  '11': { name: 'Tomato', bg: 'bg-[#d50000]', text: 'text-white' },
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

function ColorBadge({ colorId, showLabel = false }: { colorId: string | null; showLabel?: boolean }) {
  if (!colorId) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="w-3 h-3 rounded-full bg-muted border border-border" />
        {showLabel && <span className="text-xs text-muted-foreground">Default</span>}
      </span>
    )
  }
  
  const color = GOOGLE_CALENDAR_COLORS[colorId]
  if (!color) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="w-3 h-3 rounded-full bg-muted border border-border" />
        {showLabel && <span className="text-xs text-muted-foreground">#{colorId}</span>}
      </span>
    )
  }
  
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn('w-3 h-3 rounded-full', color.bg)} />
      {showLabel && <span className="text-xs text-muted-foreground">{color.name}</span>}
    </span>
  )
}

function ExecutionStatusBadge({ status }: { status: 'applied' | 'skipped' | 'failed' | 'no_match' }) {
  const variants = {
    applied: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    skipped: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    no_match: 'bg-secondary text-secondary-foreground',
  }
  
  const labels: Record<string, string> = {
    applied: 'Applied',
    skipped: 'Skipped',
    failed: 'Failed',
    no_match: 'No match',
  }
  
  return (
    <Badge variant="outline" className={cn('text-xs font-medium border-0', variants[status])}>
      {labels[status]}
    </Badge>
  )
}

const ACTION_TYPE_LABELS: Record<string, string> = {
  update_color: 'Color',
  add_attendee: 'Attendee',
  add_buffer: 'Buffer',
}

function ActionDescription({ log }: { log: ExecutionLog }) {
  const details = log.action_details

  if (log.action_type === 'update_color') {
    const colorBefore = (details?.color_before as string) ?? null
    const colorAfter = (details?.color_after as string) ?? null
    return (
      <span className="flex items-center gap-1.5">
        <ColorBadge colorId={colorBefore} />
        <span>→</span>
        <ColorBadge colorId={colorAfter} />
      </span>
    )
  }

  if (log.action_type === 'add_attendee' && details) {
    const changes = details.changes as Array<{ field: string; description: string }> | undefined
    if (changes && changes.length > 0) {
      return <span className="text-xs">{changes.map(c => c.description).join(', ')}</span>
    }
    return <span className="text-xs text-muted-foreground">Attendees updated</span>
  }

  if (log.action_type === 'add_buffer' && details) {
    const changes = details.changes as Array<{ field: string; description: string }> | undefined
    if (changes && changes.length > 0) {
      return <span className="text-xs">{changes.map(c => c.description).join(', ')}</span>
    }
    return <span className="text-xs text-muted-foreground">Buffer updated</span>
  }

  return null
}

function ExecutionCard({ 
  log, 
  onViewDetails,
  timezone
}: { 
  log: ExecutionLog
  onViewDetails: () => void
  timezone: string
}) {
  const formatEventDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A'
    return new Date(dateStr).toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timezone
    })
  }
  
  const formatExecutedAt = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timezone
    })
  }
  
  return (
    <Card className="hover:bg-muted/30 transition-colors py-0">
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-start gap-3">
          {/* Main content */}
          <div className="flex-1 min-w-0 space-y-2">
            {/* 1. Status badge + execution time */}
            <div className="flex items-center gap-2">
              <ExecutionStatusBadge status={log.execution_result} />
              <span className="text-xs text-muted-foreground">
                Executed: {formatExecutedAt(log.executed_at)}
              </span>
            </div>
            
            {/* 2. Title as hyperlink */}
            {log.event_link ? (
              <a 
                href={log.event_link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-foreground hover:text-primary hover:underline truncate block"
              >
                {log.event_title || 'Untitled Event'}
              </a>
            ) : (
              <span className="text-sm font-medium truncate block">
                {log.event_title || 'Untitled Event'}
              </span>
            )}
            
            {/* 3. Calendar ID on its own line */}
            {log.calendar_id_external && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Calendar className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{log.calendar_id_external}</span>
              </div>
            )}
            
            {/* 4. Event date + recurring icon */}
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {log.event_start_time && (
                <span>{formatEventDate(log.event_start_time)}</span>
              )}
              {log.event_is_recurring && (
                <span title="Recurring event">
                  <RefreshCw className="w-3 h-3 text-muted-foreground" />
                </span>
              )}
            </div>
            
            {/* 5. Rule name + action type badge + action description */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
              {log.rule_name ? (
                <span className="flex items-center gap-1">
                  <span className="font-medium">Rule:</span> {log.rule_name}
                </span>
              ) : log.execution_result === 'no_match' ? (
                <span className="flex items-center gap-1 italic">
                  No matching rule
                </span>
              ) : (
                <span className="flex items-center gap-1 italic">
                  Reset to default
                </span>
              )}
              
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                {ACTION_TYPE_LABELS[log.action_type] || log.action_type}
              </Badge>
              
              <ActionDescription log={log} />
            </div>
            
            {/* Error or skip reason */}
            {(log.error_message || log.skip_reason) && (
              <p className="text-xs text-muted-foreground bg-muted/50 rounded px-2 py-1 truncate">
                {log.error_message || log.skip_reason}
              </p>
            )}
          </div>
          
          {/* View action - always right aligned */}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 flex-shrink-0 self-start"
            onClick={onViewDetails}
          >
            <Eye className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function ExecutionDetailDialog({
  log,
  open,
  onClose,
  timezone
}: {
  log: ExecutionLog | null
  open: boolean
  onClose: () => void
  timezone: string
}) {
  const { session } = useAuth()
  const [isRetrying, setIsRetrying] = useState(false)
  
  if (!log) return null
  
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A'
    return new Date(dateStr).toLocaleString(undefined, { timeZone: timezone })
  }
  
  // Show retry button only for failed executions that have an event_id
  const canRetry = log.execution_result === 'failed' && log.event_id !== null
  
  const handleRetry = async () => {
    if (!session?.access_token || !log.event_id) return
    
    setIsRetrying(true)
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const edgeFunctionUrl = `${supabaseUrl}/functions/v1/automation-retry-event`
      
      const response = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          event_id: log.event_id,
          failed_executed_at: log.executed_at
        }),
      })
      
      const result = await response.json()
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to retry event update')
      }
      
      if (result.reason === 'superseded') {
        toast.info(result.message || 'A more recent rule was successfully executed on this event')
      } else if (result.success) {
        toast.success(result.message || 'Rule execution retry completed')
      } else {
        throw new Error(result.message || 'Retry failed')
      }
    } catch (error) {
      console.error('Error retrying event update:', error)
      toast.error((error as Error).message || 'Failed to retry event update')
    } finally {
      setIsRetrying(false)
    }
  }
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="
          max-w-none
          p-0
          border-0 shadow-none
          w-full h-[90vh] max-h-[90vh]
          md:w-[600px] md:h-auto md:max-h-[80vh]
          flex flex-col
          fixed !top-auto inset-x-0 !bottom-0 !left-0 !translate-x-0 !translate-y-0
          md:!left-1/2 md:!top-1/2 md:!bottom-auto md:inset-x-auto md:!-translate-x-1/2 md:!-translate-y-1/2
          rounded-t-3xl md:rounded-lg
        "
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        hideClose
      >
        <DialogDescription className="sr-only">
          Detailed information about this rule execution
        </DialogDescription>
        
        {/* Header */}
        <DialogHeader className="px-4 pt-6 pb-4 flex-shrink-0">
          <div className="flex items-center justify-between gap-4">
            <Button
              variant="secondary"
              onClick={() => onClose()}
              className="flex items-center gap-2 md:px-4 px-2 md:rounded-md rounded-full"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden md:inline">Back</span>
            </Button>

            <DialogTitle className="flex-1 text-center font-semibold text-lg">
              Execution Details
            </DialogTitle>

            <div className="w-[40px] md:w-[72px]"></div>
          </div>
        </DialogHeader>
        
        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-6 space-y-4">
          {/* Event Information */}
          <div>
            <h3 className="text-sm font-semibold mb-2 pl-1">Event Information</h3>
            <Card className="py-0">
              <CardContent className="p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-xs text-muted-foreground">Title</Label>
                    {log.event_link ? (
                      <a 
                        href={log.event_link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="font-medium text-primary hover:underline flex items-center gap-1"
                      >
                        {log.event_title || 'Untitled'}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : (
                      <p className="font-medium">{log.event_title || 'Untitled'}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Calendar</Label>
                    <p className="font-medium truncate text-xs">{log.calendar_id_external || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Start Time</Label>
                    <p className="font-medium">{formatDate(log.event_start_time)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">End Time</Label>
                    <p className="font-medium">{formatDate(log.event_end_time)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Recurring</Label>
                    <p className="font-medium">{log.event_is_recurring ? 'Yes' : 'No'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Rule Information */}
          <div>
            <h3 className="text-sm font-semibold mb-2 pl-1">Rule Information</h3>
            <Card className="py-0">
              <CardContent className="p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-xs text-muted-foreground">Rule Name</Label>
                    <p className="font-medium">
                      {log.rule_name || (log.execution_result === 'no_match' ? 'No matching rule' : 'N/A (Reset)')}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Priority</Label>
                    <p className="font-medium">{log.rule_priority ?? 'N/A'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Execution Details */}
          <div>
            <h3 className="text-sm font-semibold mb-2 pl-1">Execution Details</h3>
            <Card className="py-0">
              <CardContent className="p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-xs text-muted-foreground">Status</Label>
                    <div className="mt-1">
                      <ExecutionStatusBadge status={log.execution_result} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Executed At</Label>
                    <p className="font-medium">{formatDate(log.executed_at)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Action Type</Label>
                    <p className="font-medium">{ACTION_TYPE_LABELS[log.action_type] || log.action_type}</p>
                  </div>
                  <div className="sm:col-span-1">
                    <Label className="text-xs text-muted-foreground">Action Details</Label>
                    <div className="mt-1">
                      <ActionDescription log={log} />
                    </div>
                  </div>
                  {log.skip_reason && (
                    <div className="sm:col-span-2">
                      <Label className="text-xs text-muted-foreground">Skip Reason</Label>
                      <p className="font-medium text-yellow-600 dark:text-yellow-400">{log.skip_reason}</p>
                    </div>
                  )}
                  {log.error_message && (
                    <div className="sm:col-span-2">
                      <Label className="text-xs text-muted-foreground">Error Message</Label>
                      <p className="font-medium text-red-600 dark:text-red-400">{log.error_message}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Data */}
          <div>
            <h3 className="text-sm font-semibold mb-2 pl-1">Data</h3>
            <Card className="py-0">
              <CardContent className="p-4 space-y-4">
                {log.rule_snapshot && Object.keys(log.rule_snapshot).length > 0 && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Rule Snapshot</Label>
                    <pre className="mt-1 p-3 bg-muted rounded-md text-xs overflow-x-auto max-h-32">
                      {JSON.stringify(log.rule_snapshot, null, 2)}
                    </pre>
                    <p className="text-xs text-muted-foreground font-mono mt-1 truncate">
                      Rule ID: {log.automation_rule_id || 'Deleted/Reset'}
                    </p>
                  </div>
                )}
                {log.event_snapshot && Object.keys(log.event_snapshot).length > 0 && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Event Snapshot</Label>
                    <pre className="mt-1 p-3 bg-muted rounded-md text-xs overflow-x-auto max-h-32">
                      {JSON.stringify(log.event_snapshot, null, 2)}
                    </pre>
                    <p className="text-xs text-muted-foreground font-mono mt-1 truncate">
                      Event ID: {log.event_id || 'Deleted'}
                    </p>
                  </div>
                )}
                {(!log.rule_snapshot || Object.keys(log.rule_snapshot).length === 0) && 
                 (!log.event_snapshot || Object.keys(log.event_snapshot).length === 0) && (
                  <p className="text-sm text-muted-foreground">No snapshot data available</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* Footer - Retry Button (only for failed executions) */}
        {canRetry && (
          <div className="flex-shrink-0 px-4 py-4 border-t bg-background">
            <Button
              onClick={handleRetry}
              disabled={isRetrying}
              className="w-full"
              variant="default"
            >
              {isRetrying ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Retrying...
                </>
              ) : (
                <>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Retry event update
                </>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

function LoadingSkeleton() {
  return (
    <div className="space-y-3">
      {[...Array(5)].map((_, i) => (
        <Card key={i}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-5 w-16" />
                  <Skeleton className="h-5 w-40" />
                </div>
                <Skeleton className="h-4 w-64" />
                <Skeleton className="h-4 w-48" />
              </div>
              <Skeleton className="h-8 w-16" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function AutomationExecutionsPage() {
  const { user } = useAuth()
  const { timezone } = useSettings()
  const [searchParams, setSearchParams] = useSearchParams()
  
  // URL parameter for filtering by specific execution ID
  const urlExecutionId = searchParams.get('id')
  const hasAutoOpenedRef = useRef(false)
  
  // Data state
  const [logs, setLogs] = useState<ExecutionLog[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [calendars, setCalendars] = useState<CalendarOption[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // Pagination
  const [page, setPage] = useState(1)
  
  // Filters
  const [showFilters, setShowFilters] = useState(false)
  const [filterCalendar, setFilterCalendar] = useState<string>('all')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [filterColor, setFilterColor] = useState<string>('all')
  const [filterTitleSearch, setFilterTitleSearch] = useState('')
  const [filterRuleSearch, setFilterRuleSearch] = useState('')
  const [filterDateFrom, setFilterDateFrom] = useState('')
  const [filterDateTo, setFilterDateTo] = useState('')
  
  // Dialog state
  const [selectedLog, setSelectedLog] = useState<ExecutionLog | null>(null)
  const [detailDialogOpen, setDetailDialogOpen] = useState(false)
  
  // Fetch calendars for filter dropdown
  useEffect(() => {
    async function fetchCalendars() {
      if (!user) return
      
      const { data, error } = await supabase
        .from('automation_rules_execution_logs')
        .select('calendar_id_external')
        .eq('user_id', user.id)
        .not('calendar_id_external', 'is', null)
      
      if (error) {
        console.error('Error fetching calendars:', error)
        return
      }
      
      // Get unique calendars
      const uniqueCalendars = [...new Set(data?.map(d => d.calendar_id_external).filter(Boolean))]
      setCalendars(uniqueCalendars.map(c => ({ 
        calendar_id_external: c!, 
        label: c! 
      })))
    }
    
    fetchCalendars()
  }, [user])
  
  // Fetch logs with filters
  const fetchLogs = useCallback(async () => {
    if (!user) return
    
    setLoading(true)
    setError(null)
    
    try {
      let query = supabase
        .from('automation_rules_execution_logs')
        .select('*', { count: 'exact' })
        .eq('user_id', user.id)
        .order('executed_at', { ascending: false })
      
      // Apply URL filter for specific execution ID
      if (urlExecutionId) {
        query = query.eq('id', urlExecutionId)
      }
      
      // Apply filters
      if (filterCalendar !== 'all') {
        query = query.eq('calendar_id_external', filterCalendar)
      }
      
      if (filterStatus !== 'all') {
        query = query.eq('execution_result', filterStatus)
      }
      
      if (filterColor !== 'all') {
        query = query.eq('action_type', 'update_color')
          .contains('action_details', { color_after: filterColor })
      }
      
      if (filterTitleSearch.trim()) {
        query = query.ilike('event_title', `%${filterTitleSearch.trim()}%`)
      }
      
      if (filterRuleSearch.trim()) {
        query = query.ilike('rule_name', `%${filterRuleSearch.trim()}%`)
      }
      
      if (filterDateFrom) {
        query = query.gte('event_start_time', filterDateFrom)
      }
      
      if (filterDateTo) {
        // Add one day to include the entire end date
        const endDate = new Date(filterDateTo)
        endDate.setDate(endDate.getDate() + 1)
        query = query.lt('event_start_time', endDate.toISOString())
      }
      
      // Pagination
      const from = (page - 1) * PAGE_SIZE
      const to = from + PAGE_SIZE - 1
      query = query.range(from, to)
      
      const { data, error: queryError, count } = await query
      
      if (queryError) {
        throw queryError
      }
      
      setLogs(data || [])
      setTotalCount(count || 0)
    } catch (err) {
      console.error('Error fetching logs:', err)
      setError('Failed to load execution logs. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [user, page, filterCalendar, filterStatus, filterColor, filterTitleSearch, filterRuleSearch, filterDateFrom, filterDateTo, urlExecutionId])
  
  // Fetch logs when filters or page change
  useEffect(() => {
    fetchLogs()
  }, [fetchLogs])
  
  // Auto-open detail dialog when filtering by URL execution ID
  useEffect(() => {
    if (urlExecutionId && logs.length > 0 && !hasAutoOpenedRef.current) {
      const matchingLog = logs.find(l => l.id === urlExecutionId)
      if (matchingLog) {
        setSelectedLog(matchingLog)
        setDetailDialogOpen(true)
        hasAutoOpenedRef.current = true
      }
    }
    // Reset the ref when URL param changes
    if (!urlExecutionId) {
      hasAutoOpenedRef.current = false
    }
  }, [urlExecutionId, logs])
  
  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1)
  }, [filterCalendar, filterStatus, filterColor, filterTitleSearch, filterRuleSearch, filterDateFrom, filterDateTo])
  
  const totalPages = Math.ceil(totalCount / PAGE_SIZE)
  const hasActiveFilters = filterCalendar !== 'all' || filterStatus !== 'all' || filterColor !== 'all' || 
    filterTitleSearch.trim() !== '' || filterRuleSearch.trim() !== '' || filterDateFrom !== '' || filterDateTo !== ''
  
  const clearFilters = () => {
    setFilterCalendar('all')
    setFilterStatus('all')
    setFilterColor('all')
    setFilterTitleSearch('')
    setFilterRuleSearch('')
    setFilterDateFrom('')
    setFilterDateTo('')
  }
  
  const clearUrlFilter = () => {
    searchParams.delete('id')
    setSearchParams(searchParams)
    hasAutoOpenedRef.current = false
  }
  
  const handleViewDetails = (log: ExecutionLog) => {
    setSelectedLog(log)
    setDetailDialogOpen(true)
  }
  
  return (
    <div className="main-content">
      {/* URL Filter Banner */}
      {urlExecutionId && (
        <div className="flex items-center justify-between gap-2 mb-4 p-3 bg-primary/10 border border-primary/20 rounded-lg">
          <span className="text-sm">
            Showing execution: <code className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">{urlExecutionId}</code>
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={clearUrlFilter}
            className="gap-1.5 h-7"
          >
            <X className="h-3.5 w-3.5" />
            Show all
          </Button>
        </div>
      )}
      
      {/* Header actions */}
      <div className="flex items-center justify-end gap-2 mb-6">
        <div className="flex items-center gap-2">
          <Button
            variant={showFilters ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="gap-2"
          >
            <Filter className="h-4 w-4" />
            Filters
            {hasActiveFilters && (
              <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 justify-center">
                !
              </Badge>
            )}
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={fetchLogs}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
      
      {/* Filters Panel */}
      {showFilters && (
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Calendar filter */}
              <div className="space-y-2">
                <Label className="text-xs">Calendar</Label>
                <Select value={filterCalendar} onValueChange={setFilterCalendar}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="All calendars" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All calendars</SelectItem>
                    {calendars.map(cal => (
                      <SelectItem key={cal.calendar_id_external} value={cal.calendar_id_external}>
                        {cal.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Status filter */}
              <div className="space-y-2">
                <Label className="text-xs">Status</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="applied">Applied</SelectItem>
                    <SelectItem value="skipped">Skipped</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="no_match">No match</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Color filter */}
              <div className="space-y-2">
                <Label className="text-xs">Color Applied</Label>
                <Select value={filterColor} onValueChange={setFilterColor}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="All colors" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All colors</SelectItem>
                    {Object.entries(GOOGLE_CALENDAR_COLORS).map(([id, color]) => (
                      <SelectItem key={id} value={id}>
                        <span className="flex items-center gap-2">
                          <span className={cn('w-3 h-3 rounded-full', color.bg)} />
                          {color.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Title search */}
              <div className="space-y-2">
                <Label className="text-xs">Event Title</Label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search title..."
                    value={filterTitleSearch}
                    onChange={(e) => setFilterTitleSearch(e.target.value)}
                    className="h-9 pl-9"
                  />
                </div>
              </div>
              
              {/* Rule name search */}
              <div className="space-y-2">
                <Label className="text-xs">Rule Name</Label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search rule..."
                    value={filterRuleSearch}
                    onChange={(e) => setFilterRuleSearch(e.target.value)}
                    className="h-9 pl-9"
                  />
                </div>
              </div>
              
              {/* Date range */}
              <div className="space-y-2">
                <Label className="text-xs">Event Date Range</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={filterDateFrom}
                    onChange={(e) => setFilterDateFrom(e.target.value)}
                    className="h-9 text-sm"
                  />
                  <span className="text-muted-foreground">-</span>
                  <Input
                    type="date"
                    value={filterDateTo}
                    onChange={(e) => setFilterDateTo(e.target.value)}
                    className="h-9 text-sm"
                  />
                </div>
              </div>
            </div>
            
            {/* Clear filters */}
            {hasActiveFilters && (
              <div className="mt-4 pt-4 border-t">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="gap-2 text-muted-foreground"
                >
                  <X className="h-4 w-4" />
                  Clear all filters
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Results count */}
      <div className="flex items-center justify-between mb-4 text-sm text-muted-foreground">
        <span>
          {totalCount} execution{totalCount !== 1 ? 's' : ''} found
        </span>
        {totalPages > 1 && (
          <span>
            Page {page} of {totalPages}
          </span>
        )}
      </div>
      
      {/* Error state */}
      {error && (
        <Card className="mb-4 border-destructive">
          <CardContent className="p-4 text-destructive text-sm">
            {error}
          </CardContent>
        </Card>
      )}
      
      {/* Loading state */}
      {loading && <LoadingSkeleton />}
      
      {/* Empty state */}
      {!loading && logs.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <div className="text-muted-foreground">
              {hasActiveFilters ? (
                <>
                  <p className="mb-2">No executions match your filters.</p>
                  <Button variant="link" onClick={clearFilters}>
                    Clear filters
                  </Button>
                </>
              ) : (
                <p>No automation executions yet. Create some automation rules to get started!</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Logs list */}
      {!loading && logs.length > 0 && (
        <div className="space-y-3">
          {logs.map(log => (
            <ExecutionCard
              key={log.id}
              log={log}
              onViewDetails={() => handleViewDetails(log)}
              timezone={timezone}
            />
          ))}
        </div>
      )}
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          
          <div className="flex items-center gap-1">
            {/* Show page numbers */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number
              if (totalPages <= 5) {
                pageNum = i + 1
              } else if (page <= 3) {
                pageNum = i + 1
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i
              } else {
                pageNum = page - 2 + i
              }
              
              return (
                <Button
                  key={pageNum}
                  variant={pageNum === page ? 'default' : 'ghost'}
                  size="sm"
                  className="w-8 h-8 p-0"
                  onClick={() => setPage(pageNum)}
                  disabled={loading}
                >
                  {pageNum}
                </Button>
              )
            })}
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages || loading}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
      
      {/* Detail Dialog */}
      <ExecutionDetailDialog
        log={selectedLog}
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        timezone={timezone}
      />
    </div>
  )
}

